import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, } from "react-router-dom";
import useAnalytics from '../../hooks/useAnalytics';
import Homepage from '../HomePage/HomePage'
import AboutUsPage from '../AboutUsPage/AboutUsPage';
import AffordableHousingPage from '../AffordableHousingPage/AffordableHousingPage';
import HousingPipelinePage from '../HousingPipelinePage/HousingPipelinePage';
import HousingCountyPage from '../HousingCountyPage/HousingCountyPage';
import HousingTypePage from '../HousingTypePage/HousingTypePage';
import HousingDeveloperPage from '../HousingDeveloperPage/HousingDeveloperPage';
import ManagedItOfferPage from '../ManagedItOfferPage/ManagedItOfferPage';
import PricingPage from '../PricingPage/PricingPage';
import PortfolioPage from '../PortfolioPage/PortfolioPage';
import PortfolioSinglePage from '../PortfolioSinglePage/PortfolioSinglePage';
import ServicePage from '../ServicePage/ServicePage';
import ServiceSinglePage from '../ServiceSinglePage/ServiceSinglePage';
import ItServicesPage from '../ItServicesPage/ItServicesPage';
import SameDayWebsitesPage from '../SameDayWebsitesPage/SameDayWebsitesPage';
import BlogPage from '../BlogPage/BlogPage';
import BlogDetails from '../BlogDetails/BlogDetails';
import ContactPage from '../ContactPage/ContactPage';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import ManagedItCalculatorPage from '../ManagedItCalculatorPage/ManagedItCalculatorPage';
import TcacBroadbandEstimatorPage from '../TcacBroadbandEstimatorPage/TcacBroadbandEstimatorPage';
import AmiLimitsPage from '../AmiLimitsPage/AmiLimitsPage';
import DasErccsCheckerPage from '../DasErccsCheckerPage/DasErccsCheckerPage';
import AreaOfRefugeCheckerPage from '../AreaOfRefugeCheckerPage/AreaOfRefugeCheckerPage';
import LowVoltageBudgetPage from '../LowVoltageBudgetPage/LowVoltageBudgetPage';
import CyberSecurityScorecardPage from '../CyberSecurityScorecardPage/CyberSecurityScorecardPage';
import WebsiteAuditPage from '../WebsiteAuditPage/WebsiteAuditPage';
import PressureTestPage from '../PressureTestPage/PressureTestPage';
import AiReadinessAssessmentPage from '../AiReadinessAssessmentPage/AiReadinessAssessmentPage';
import ToolStudioPage from '../ToolStudioPage/ToolStudioPage';
import LowVoltageRequestPage from '../LowVoltageRequestPage/LowVoltageRequestPage';
import TcacScoringEstimatorPage from '../TcacScoringEstimatorPage/TcacScoringEstimatorPage';
import CloudMigrationEstimatorPage from '../CloudMigrationEstimatorPage/CloudMigrationEstimatorPage';
import CtcacDeadlinesPage from '../CtcacDeadlinesPage/CtcacDeadlinesPage';
import FeasibilityProFormaPage from '../FeasibilityProFormaPage/FeasibilityProFormaPage';
import LocationsHubPage from '../LocationsHubPage/LocationsHubPage';
import LocationPage from '../LocationPage/LocationPage';
import StartupPackagePage from '../StartupPackagePage/StartupPackagePage';
import ClientOnboardingPage from '../ClientOnboardingPage/ClientOnboardingPage';
import CustomSoftwarePage from '../CustomSoftwarePage/CustomSoftwarePage';
import CncMachineBuildersPage from '../CncMachineBuildersPage/CncMachineBuildersPage';
import IndicatorPage from '../IndicatorPage/IndicatorPage';
import IndicatorsHubPage from '../IndicatorsHubPage/IndicatorsHubPage';
import IndicatorSinglePage from '../IndicatorSinglePage/IndicatorSinglePage';

// The client portal is lazy-loaded — it's the only part of the app that pulls
// in @supabase/supabase-js, and it's never prerendered (deliberately kept out
// of package.json's reactSnap.include since it's auth-gated/dynamic), so there's
// no reason for its code to ship in the marketing site's main bundle.
const ClientLoginPage = lazy(() => import('../ClientPortal/ClientLoginPage/ClientLoginPage'));
const RequireClientAuth = lazy(() => import('../../components/RequireClientAuth/RequireClientAuth'));
const ClientDashboardPage = lazy(() => import('../ClientPortal/ClientDashboardPage/ClientDashboardPage'));
const ClientRequestDetailPage = lazy(() => import('../ClientPortal/ClientRequestDetailPage/ClientRequestDetailPage'));
const ClientProposalPage = lazy(() => import('../ClientPortal/ClientProposalPage/ClientProposalPage'));

// Staff admin area — same lazy/never-prerendered treatment as /portal/*.
const AdminLoginPage = lazy(() => import('../AdminPortal/AdminLoginPage/AdminLoginPage'));
const RequireStaffAuth = lazy(() => import('../../components/RequireStaffAuth/RequireStaffAuth'));
const AdminDashboardPage = lazy(() => import('../AdminPortal/AdminDashboardPage/AdminDashboardPage'));
const AdminRequestDetailPage = lazy(() => import('../AdminPortal/AdminRequestDetailPage/AdminRequestDetailPage'));
const AdminNewProjectPage = lazy(() => import('../AdminPortal/AdminNewProjectPage/AdminNewProjectPage'));
const AdminProposalPage = lazy(() => import('../AdminPortal/AdminProposalPage/AdminProposalPage'));
const FloorplanEditorPage = lazy(() => import('../AdminPortal/FloorplanEditorPage/FloorplanEditorPage'));
const ProposalDocumentsPage = lazy(() => import('../AdminPortal/ProposalDocumentsPage/ProposalDocumentsPage'));
// Public token-shared proposal view — login-free, authorized by the token
// itself (validated server-side by the buildlab-shared-proposal edge function).
const ProposalSharePage = lazy(() => import('../ProposalSharePage/ProposalSharePage'));

// Must live inside <BrowserRouter> so useLocation() works. Fires GA4
// page_view on every route change.
const AnalyticsTracker = () => {
  useAnalytics();
  return null;
};

const AllRoute = () => {

  return (
    <div className="App">
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="home" element={<Homepage />} />
          <Route path="about" element={<AboutUsPage />} />
          <Route path="affordable-housing" element={<AffordableHousingPage />} />
          <Route path="housing-pipeline" element={<HousingPipelinePage />} />
          <Route path="housing-pipeline/county/:countySlug" element={<HousingCountyPage />} />
          <Route path="housing-pipeline/type/:typeSlug" element={<HousingTypePage />} />
          <Route path="housing-pipeline/developer/:devSlug" element={<HousingDeveloperPage />} />
          <Route path="managed-it-offer" element={<ManagedItOfferPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="portfolio" element={<PortfolioPage />} />
          <Route path="portfolio-details/:slug" element={<PortfolioSinglePage />} />
          <Route path="service" element={<ServicePage />} />
          <Route path="it-services" element={<ItServicesPage />} />
          <Route path="it-cost-calculator" element={<ManagedItCalculatorPage />} />
          <Route path="tcac-broadband-estimator" element={<TcacBroadbandEstimatorPage />} />
          <Route path="income-rent-limits" element={<AmiLimitsPage />} />
          <Route path="das-erccs-checker" element={<DasErccsCheckerPage />} />
          <Route path="area-of-refuge-checker" element={<AreaOfRefugeCheckerPage />} />
          <Route path="low-voltage-budget-estimator" element={<LowVoltageBudgetPage />} />
          <Route path="cyber-security-scorecard" element={<CyberSecurityScorecardPage />} />
          <Route path="website-audit" element={<WebsiteAuditPage />} />
          <Route path="proforma-pressure-test" element={<PressureTestPage />} />
          <Route path="ai-readiness-assessment" element={<AiReadinessAssessmentPage />} />
          <Route path="tool-studio" element={<ToolStudioPage />} />
          <Route path="low-voltage-project-request" element={<LowVoltageRequestPage />} />
          <Route path="tcac-scoring-estimator" element={<TcacScoringEstimatorPage />} />
          <Route path="cloud-migration-estimator" element={<CloudMigrationEstimatorPage />} />
          <Route path="ctcac-deadlines" element={<CtcacDeadlinesPage />} />
          <Route path="feasibility-pro-forma" element={<FeasibilityProFormaPage />} />
          <Route path="locations" element={<LocationsHubPage />} />
          <Route path="locations/:slug" element={<LocationPage />} />
          <Route path="same-day-websites" element={<SameDayWebsitesPage />} />
          <Route path="startup-package" element={<StartupPackagePage />} />
          <Route path="custom-software" element={<CustomSoftwarePage />} />
          <Route path="cnc-machine-builders" element={<CncMachineBuildersPage />} />
          <Route path="institutional-indicator" element={<IndicatorPage />} />
          <Route path="indicators" element={<IndicatorsHubPage />} />
          <Route path="indicators/:slug" element={<IndicatorSinglePage />} />
          <Route path="client-onboarding" element={<ClientOnboardingPage />} />
          <Route path="service-single/:slug" element={<ServiceSinglePage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog-single/:slug" element={<BlogDetails />} />
          <Route path="contact" element={<ContactPage />} />

          <Route path="portal/login" element={<Suspense fallback={null}><ClientLoginPage /></Suspense>} />
          <Route path="portal" element={<Suspense fallback={null}><RequireClientAuth /></Suspense>}>
            <Route index element={<ClientDashboardPage />} />
            <Route path="requests/:requestId" element={<ClientRequestDetailPage />} />
            <Route path="proposals/:proposalId" element={<ClientProposalPage />} />
          </Route>

          <Route path="proposal-share/:token" element={<Suspense fallback={null}><ProposalSharePage /></Suspense>} />

          <Route path="admin/login" element={<Suspense fallback={null}><AdminLoginPage /></Suspense>} />
          <Route path="admin" element={<Suspense fallback={null}><RequireStaffAuth /></Suspense>}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="requests/new" element={<AdminNewProjectPage />} />
            <Route path="requests/:requestId" element={<AdminRequestDetailPage />} />
            <Route path="proposals/:proposalId" element={<AdminProposalPage />} />
            <Route path="proposals/:proposalId/documents" element={<ProposalDocumentsPage />} />
            <Route path="floorplans/:floorplanId" element={<FloorplanEditorPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>

    </div>
  );
}

export default AllRoute;
